import { Pencil, X } from "lucide-react";
import { type FormEvent, type SyntheticEvent, useState } from "react";

import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import { useUpdatePassenger } from "../../api/use-claim-mutations";
import { isValidEmail, type PassengerRow } from "./passenger-grouping";

/**
 * The actions live inside rows that react to the pointer (drag on the family board, selection in
 * the passenger table). The dialogs are portaled, but React still bubbles their events through
 * this tree, so everything stops here.
 */
export function stopPropagation(event: SyntheticEvent) {
  event.stopPropagation();
}

export const ACTION_CLASS =
  "shrink-0 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 data-popup-open:opacity-100";

/**
 * Edit (pencil) or remove (red ×) the address on file for a passenger, shown on hover next to it.
 *
 * Removing is for an address that belongs to somebody else — a co-passenger answering « Même adresse
 * que moi » for a family they do not speak for (TECH-1380). Without an address the passenger is
 * asked for again in the « contacts passagers » step of the next signature.
 *
 * Only an address carried by the passenger's own `UserClaim` can be removed: one shown from the
 * family acquisition or the user account is not theirs to forget here.
 */
export function PassengerEmailActions({ claimId, row }: { claimId: string; row: PassengerRow }) {
  const [editOpen, setEditOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [email, setEmail] = useState("");
  const updatePassenger = useUpdatePassenger(claimId);

  const userSegmentId = row.id;
  const contact = row.passenger.contact;
  if (userSegmentId === null || !contact) {
    return null;
  }

  function openEdit() {
    setEmail(contact ?? "");
    setEditOpen(true);
  }

  function save(event: FormEvent) {
    event.preventDefault();
    if (userSegmentId === null || !isValidEmail(email)) {
      return;
    }
    updatePassenger.mutate(
      { userSegmentId, email: email.trim() },
      { onSuccess: ({ success }) => success && setEditOpen(false) },
    );
  }

  function remove() {
    if (userSegmentId === null) {
      return;
    }
    updatePassenger.mutate(
      { userSegmentId, email: null },
      { onSuccess: ({ success }) => success && setRemoveOpen(false) },
    );
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: only stops the row's drag / selection handlers
    // biome-ignore lint/a11y/useKeyWithClickEvents: only stops the row's drag / selection handlers
    <span
      className="inline-flex shrink-0 items-center"
      onPointerDown={stopPropagation}
      onClick={stopPropagation}
    >
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label={`Modifier l'adresse e-mail de ${row.passenger.civility}`}
              className={cn(ACTION_CLASS, "size-5 text-gray-400 hover:text-gray-700 sm:size-5")}
              onClick={openEdit}
            />
          }
        >
          <Pencil className="size-3" aria-hidden="true" />
        </TooltipTrigger>
        <TooltipContent>Modifier l&apos;adresse e-mail</TooltipContent>
      </Tooltip>

      {row.ownEmail ? (
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label={`Retirer l'adresse e-mail de ${row.passenger.civility}`}
                className={cn(
                  ACTION_CLASS,
                  "size-5 text-red-500 hover:bg-red-50 hover:text-red-600 sm:size-5",
                )}
                onClick={() => setRemoveOpen(true)}
              />
            }
          >
            <X className="size-3.5" aria-hidden="true" />
          </TooltipTrigger>
          <TooltipContent>Retirer l&apos;adresse e-mail</TooltipContent>
        </Tooltip>
      ) : null}

      <AlertDialog open={removeOpen} onOpenChange={setRemoveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Retirer l&apos;adresse de {row.passenger.civility} ?</AlertDialogTitle>
            <AlertDialogDescription>
              « {contact} » ne sera plus l&apos;adresse de ce passager. Elle sera redemandée lors de
              l&apos;envoi de la signature.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>Annuler</AlertDialogClose>
            <Button variant="destructive" loading={updatePassenger.isPending} onClick={remove}>
              Retirer l&apos;adresse
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-md">
          {/* `contents`: header, panel and footer stay the popup's own flex children. */}
          <form className="contents" onSubmit={save}>
            <DialogHeader>
              <DialogTitle>Adresse e-mail de {row.passenger.civility}</DialogTitle>
            </DialogHeader>
            <DialogPanel>
              <Field>
                <FieldLabel>Adresse e-mail</FieldLabel>
                <Input
                  type="email"
                  autoComplete="off"
                  autoFocus
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </Field>
            </DialogPanel>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>
                Annuler
              </Button>
              <Button
                type="submit"
                loading={updatePassenger.isPending}
                disabled={!isValidEmail(email) || email.trim() === contact}
              >
                Enregistrer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </span>
  );
}
